import { useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import {
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import { base44 } from "../../api/base44Client";

export default function FileUpload({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Formato non supportato. Usa JPG, PNG, WebP o PDF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Il file è troppo grande. Dimensione massima: 10 MB.");
      return;
    }

    setUploading(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    setPreview(file_url);
    onChange(file_url);
    setUploading(false);
  };

  const handleRemove = () => {
    setPreview(null);
    onChange("");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      {preview ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {preview.match(/\.(jpg|jpeg|png|webp)/i) ? (
            <Box
              component="img"
              src={preview}
              alt={label}
              sx={{
                width: 64,
                height: 64,
                objectFit: "cover",
                borderRadius: 1,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.light",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileText color="white" />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              Documento caricato
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Clicca × per rimuovere
            </Typography>
          </Box>
          <IconButton
            onClick={handleRemove}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <X size={18} />
          </IconButton>
        </Paper>
      ) : (
        <Box
          component="label"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.light",
            },
          }}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Upload size={24} style={{ color: "#9e9e9e" }} />
              <Typography variant="body2" color="text.secondary">
                Clicca per caricare
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "10px" }}
              >
                JPG, PNG, WebP o PDF · Max 10 MB
              </Typography>
            </>
          )}
          <input
            type="file"
            style={{ display: "none" }}
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            onChange={handleUpload}
            disabled={uploading}
          />
        </Box>
      )}
    </Box>
  );
}
